import { Role } from 'client/enums/Role'
import { store as klubbleStore, type StoreState } from 'client/store'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

interface Abilities {
  canInvite: boolean
  canUseDailyDose: boolean
  canSeeAccount: boolean
  canSeeInfoMenuItem: boolean
  canSeeRecentPuzzles: boolean
  canGiveFeedback: boolean
  canSeeMenu: boolean
  canSeePuzzlePlayerBreadcrumbs: boolean
  canSeeCollapsedDailyDoseEndscreen: boolean
  canFavoritePuzzles: boolean
  canSeeHomeInfoContent: boolean
  canSeeHomeSuggestions: boolean
  canSeeHomeMinis: boolean
  canSeeHomeExploreCarousel: boolean
  canSeeHomeFreePuzzlesCarousel: boolean
  canDirectlyStartTodaysFreePuzzleFromHome: boolean
}

const useAbilities = (): Abilities => {
  const roles = useSelector<StoreState, Role[]>((state) => state.user?.roles)
  // This hook can be called from within the puzzle player where the state is not the klubble store,
  // resulting in the roles being undefined, therefore we use the klubble store as a fallback.
  return useMemo<Abilities>(() => getAbilities(roles || klubbleStore.getState().user?.roles || []), [roles])
}

export default useAbilities

export const getAbilities = (roles: Role[]): Abilities => ({
  canInvite: roles.includes(Role.identified),
  canUseDailyDose: roles.includes(Role.identified),
  canSeeAccount: roles.includes(Role.identified),
  canSeeRecentPuzzles: roles.includes(Role.identified),
  canSeeInfoMenuItem: roles.includes(Role.anonymous),
  canGiveFeedback: !!roles.length,
  canSeeMenu: !!roles.length,
  canSeePuzzlePlayerBreadcrumbs: roles.includes(Role.identified),
  canSeeCollapsedDailyDoseEndscreen: roles.includes(Role.anonymous),
  canFavoritePuzzles: roles.includes(Role.identified),
  canSeeHomeInfoContent: roles.includes(Role.anonymous),
  canSeeHomeSuggestions: roles.includes(Role.identified),
  canSeeHomeMinis: roles.includes(Role.identified),
  canSeeHomeExploreCarousel: roles.includes(Role.anonymous),
  canSeeHomeFreePuzzlesCarousel: !roles.includes(Role.subscriber),
  canDirectlyStartTodaysFreePuzzleFromHome: roles.includes(Role.anonymous),
})
